export type FeedbackRating = "ThumbsUp" | "ThumbsDown";

export const FEEDBACK_CATEGORIES = [
  "Inaccurate",
  "Unhelpful",
  "Incomplete",
  "PoorFormatting",
  "WrongTone",
  "Other",
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

export interface MessageFeedbackData {
  rating: FeedbackRating;
  categories?: FeedbackCategory[];
  comment?: string;
}

export type MessageFeedbackRequest = MessageFeedbackData;

export interface MessageFeedbackResponse extends MessageFeedbackData {
  messageId: string;
}

export const RATING_MAP: Record<string, FeedbackRating> = {
  thumbs_up: "ThumbsUp",
  thumbs_down: "ThumbsDown",
  ThumbsUp: "ThumbsUp",
  ThumbsDown: "ThumbsDown",
};

const VALID_CATEGORIES: Set<FeedbackCategory> = new Set(FEEDBACK_CATEGORIES);

/** Galactus stores ratings as snake_case. The chat UI uses PascalCase. */
export function normalizeFeedback(raw: unknown): MessageFeedbackData | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const feedback = raw as Record<string, unknown>;
  if (!feedback.rating) return undefined;

  const rating = RATING_MAP[feedback.rating as string];
  if (!rating) return undefined;

  let comment: string | undefined;
  if (typeof feedback.comment === "string") {
    const trimmed = feedback.comment.trim();
    if (trimmed.length > 0) comment = trimmed;
  }

  let categories: FeedbackCategory[] | undefined;
  if (Array.isArray(feedback.categories)) {
    const valid = feedback.categories.filter(
      (category): category is FeedbackCategory =>
        typeof category === "string" && VALID_CATEGORIES.has(category as FeedbackCategory),
    );
    if (valid.length > 0) categories = valid;
  }

  return { rating, categories, comment };
}

export function messageFeedbackPath(conversationId: string, messageId: string): string {
  return `/.internal/conversations/${conversationId}/messages/${messageId}/feedback`;
}
